/**
 * Biometric app lock (native shell only).
 *
 * Opt-in device-local setting (Settings → Sicherheit): when enabled, the app
 * demands Face ID / Touch ID / device credential on cold start and when it
 * returns to the foreground after more than `BACKGROUND_LOCK_AFTER_MS` in the
 * background. The session id sits in the keychain for 30 days — this puts a
 * biometric latch in front of it without touching the auth stack: the lock is
 * a UI overlay (AppLockGate), nothing about tokens or sessions changes.
 *
 * Feature detection everywhere: OTA-updated web bundles run inside shells
 * that predate the `@aparajita/capacitor-biometric-auth` plugin. On those the
 * dynamic import or `checkBiometry` fails, `getBiometryLabel()` resolves
 * `null`, the settings toggle stays hidden and no lock is ever armed.
 */
import { create } from 'zustand';

import { isNativeShell } from './platform';

const STORAGE_KEY = '9brains.app-lock-enabled';
/** Foreground returns within this window skip the lock (quick app switches). */
const BACKGROUND_LOCK_AFTER_MS = 60_000;

export type BiometryLabel = 'faceId' | 'touchId' | 'generic';

interface AppLockState {
  locked: boolean;
  /**
   * False while the app is in the background, until its return has been
   * judged for the lock. What waits for the app to be back (a voice start
   * from the Action Button, KAN-1907) waits for this, not for `!locked`.
   */
  appActive: boolean;
  lock: () => void;
  unlock: () => void;
}

export const useAppLockStore = create<AppLockState>((set) => ({
  locked: false,
  appActive: true,
  lock: () => set({ locked: true }),
  unlock: () => set({ locked: false }),
}));

export function isAppLockEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setAppLockEnabled(enabled: boolean): void {
  try {
    if (enabled) {
      localStorage.setItem(STORAGE_KEY, '1');
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Storage unavailable — the toggle simply won't stick.
  }
}

/**
 * Which unlock method the device offers, or `null` when the app lock cannot
 * work here (web, old shell without the plugin, no biometry AND no passcode).
 * Drives both the settings-toggle visibility and its wording.
 */
export async function getBiometryLabel(): Promise<BiometryLabel | null> {
  if (!isNativeShell()) return null;
  try {
    const { BiometricAuth, BiometryType } = await import('@aparajita/capacitor-biometric-auth');
    const check = await BiometricAuth.checkBiometry();
    if (check.isAvailable) {
      if (check.biometryType === BiometryType.faceId) return 'faceId';
      if (check.biometryType === BiometryType.touchId) return 'touchId';
      return 'generic';
    }
    // No biometry enrolled, but a passcode/PIN still allows the credential
    // fallback of authenticate().
    return check.deviceIsSecure ? 'generic' : null;
  } catch {
    return null; // plugin missing (older shell) — feature stays invisible
  }
}

/**
 * Present the system unlock prompt. Resolves `true` on success, `false` on
 * cancel/failure — never throws (the gate shows a retry button either way).
 */
export async function authenticateAppLock(reason: string): Promise<boolean> {
  try {
    const { BiometricAuth } = await import('@aparajita/capacitor-biometric-auth');
    await BiometricAuth.authenticate({
      reason,
      allowDeviceCredential: true,
    });
    return true;
  } catch {
    return false;
  }
}

let backgroundedAt: number | null = null;

/**
 * The app went to the background or came back. Coming back after a longer
 * stay locks it, and the lock lands in the same update as `appActive`, so
 * nothing waiting for the return can slip in before it.
 */
export function onAppStateChange(isActive: boolean): void {
  if (!isActive) {
    backgroundedAt ??= Date.now();
    useAppLockStore.setState({ appActive: false });
    return;
  }
  const away = backgroundedAt ? Date.now() - backgroundedAt : 0;
  backgroundedAt = null;
  const lockNow = isAppLockEnabled() && away > BACKGROUND_LOCK_AFTER_MS;
  useAppLockStore.setState(lockNow ? { appActive: true, locked: true } : { appActive: true });
}

/**
 * Arm the lock. Called once from main.tsx in the native shell, BEFORE first
 * paint: on cold start with the lock enabled the store starts out locked, so
 * the gate covers the very first frame. Also watches app-state changes to
 * re-lock after a longer background stay.
 */
export function initAppLock(): void {
  if (!isNativeShell()) return;

  if (isAppLockEnabled()) {
    useAppLockStore.getState().lock();
  }

  void import('@capacitor/app')
    .then(({ App }) =>
      App.addListener('appStateChange', ({ isActive }) => onAppStateChange(isActive))
    )
    .catch(() => {
      // @capacitor/app unavailable — cold-start lock still works.
    });
}
